import { cn } from "@babybjorn/ui";
import {
	useState,
	type FC,
	type PropsWithChildren,
	type ReactNode,
} from "react";
import Badge from "../../components/general/Badge";
import type { TypedMoney } from "@commercetools/platform-sdk";
import { moneyToNumber } from "../../../../utils/commercetools/money";
import { useHasMousePointer } from "../../hooks/use-is-screensize";
import { formatMoney } from "../../utils/cart/currency";
import type { Image } from "../../utils/storyblok/product-variant";
import HoverImageSwap from "../general/HoverImageSwap/HoverImageSwap";
import * as s from "./ProductCard.module.scss";

export type ProductCardProps = PropsWithChildren<{
	image: ReactNode;
	altImage?: ReactNode;
	name: string;
	rawPrice: TypedMoney;
	fabric?: string | null;
	color: string;
	isNew?: boolean;
	newLabel?: string;
	linkComponent: (children: ReactNode) => ReactNode;
	url?: string;
	inModule?: boolean;
	isInGrid?: boolean;
	originalImages?: Image[];
	isOnStock?: boolean;
	productType?: string;
}>;

const ProductCard: FC<ProductCardProps> = ({
	image,
	altImage,
	name,
	rawPrice,
	fabric,
	color,
	isNew = false,
	newLabel,
	linkComponent,
	children,
	inModule,
	isInGrid,
}) => {
	//Should be li if used in the product slider, otherwise not.
	const Wrapper = inModule ? "div" : "li";
	const [hover, setHover] = useState(false);
	const hasMousePointer = useHasMousePointer();

	return (
		<Wrapper
			itemScope
			itemType="https://schema.org/Product"
			className={cn(s.productCard, {
				[s.hasAltImage]: hasMousePointer && altImage,
				[s.moduleCard]: inModule,
				[s.isInGrid]: isInGrid,
			})}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<meta
				itemProp="name"
				content={`${name} - ${fabric ? ` ${fabric},` : ""} ${color}`}
			/>
			{linkComponent(
				<div
					className={cn(s.imgContainer, {
						[s.moduleRatio]: inModule,
					})}
				>
					<HoverImageSwap
						imageNode={image}
						hoverImageNode={altImage}
						hover={hover}
					/>
					{isNew && newLabel && <Badge labels={[newLabel]} />}
				</div>,
			)}
			<div className={cn(s.detailsContainer, { [s.moduleDetails]: inModule })}>
				<div>
					<div
						itemScope
						itemProp="offers"
						itemType="https://schema.org/Offer"
						className={s.nameAndPrice}
					>
						<meta itemProp="priceCurrency" content={rawPrice.currencyCode} />
						<meta
							itemProp="price"
							content={moneyToNumber(rawPrice).toString()}
						/>
						{linkComponent(<h3>{name}</h3>)}
						<h3>{formatMoney(rawPrice)}</h3>
					</div>
					<p className={s.fabricAndColor}>
						{fabric ? `${fabric},` : ""} {color}
					</p>
				</div>
				{inModule && <hr className={s.divider} />}
				<div className={s.childrenContainer}>{children}</div>
			</div>
		</Wrapper>
	);
};

export default ProductCard;
